import {
  get,
  getWithHeaders,
  patchWithHeaders,
  postFormData,
  postWithHeaders,
  type RequestOptions,
} from '@/api/client';
import type {
  FormFillSession,
  FormSignatureUploadResponse,
  PublicFormAutosaveInput,
  PublicFormAutosaveResponse,
  PublicFormGateResponse,
  PublicFormSubmitInput,
  PublicFormSubmitResponse,
  PublicFormVerifyInput,
  PublicFormVerifyResponse,
} from './types';

const SESSION_HEADER = 'X-Form-Session';
const CLIENT_SESSION_HEADER = 'X-Form-Client-Session';
const CLIENT_SESSION_STORAGE_KEY = 'elva.forms.clientSession';

/** Stable per-browser id sent for patient-fill open tracking (FD18). */
function getClientSessionId(): string {
  try {
    const existing = window.localStorage.getItem(CLIENT_SESSION_STORAGE_KEY);
    if (existing) return existing;
    const generated = crypto.randomUUID();
    window.localStorage.setItem(CLIENT_SESSION_STORAGE_KEY, generated);
    return generated;
  } catch {
    return crypto.randomUUID();
  }
}

function clientHeaders(): Record<string, string> {
  return { [CLIENT_SESSION_HEADER]: getClientSessionId() };
}

function sessionHeaders(sessionToken: string): Record<string, string> {
  return { [SESSION_HEADER]: sessionToken, ...clientHeaders() };
}

export const publicFormsApi = {
  getGate: (token: string, options?: RequestOptions) =>
    getWithHeaders<PublicFormGateResponse>(
      `/api/public/forms/${token}`,
      clientHeaders(),
      undefined,
      options,
    ),

  verifyDob: (token: string, data: PublicFormVerifyInput) =>
    postWithHeaders<PublicFormVerifyResponse>(
      `/api/public/forms/${token}/verify`,
      data,
      clientHeaders(),
    ),

  getSession: (token: string, sessionToken: string, locale = 'en', options?: RequestOptions) =>
    getWithHeaders<FormFillSession>(
      `/api/public/forms/${token}/session`,
      sessionHeaders(sessionToken),
      { locale },
      options,
    ),

  autosave: (token: string, sessionToken: string, data: PublicFormAutosaveInput) =>
    patchWithHeaders<PublicFormAutosaveResponse>(
      `/api/public/forms/${token}/autosave`,
      data,
      sessionHeaders(sessionToken),
    ),

  submit: (token: string, sessionToken: string, data: PublicFormSubmitInput) =>
    postWithHeaders<PublicFormSubmitResponse>(
      `/api/public/forms/${token}/submit`,
      data,
      sessionHeaders(sessionToken),
    ),

  uploadSignature: (token: string, sessionToken: string, file: File, fieldKey = 'default') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('field_key', fieldKey);
    return postFormData<FormSignatureUploadResponse>(
      `/api/public/forms/${token}/signature`,
      formData,
      sessionHeaders(sessionToken),
    );
  },

  uploadSignatureBase64: (
    token: string,
    sessionToken: string,
    imageData: string,
    fieldKey = 'default',
  ) =>
    postWithHeaders<FormSignatureUploadResponse>(
      `/api/public/forms/${token}/signature`,
      { image_data: imageData, field_key: fieldKey },
      sessionHeaders(sessionToken),
    ),
};

export const staffPatientFormsApi = {
  getSession: (token: string, options?: RequestOptions) =>
    get<FormFillSession>(`/api/forms/patient/${token}`, undefined, options),

  autosave: (token: string, data: PublicFormAutosaveInput) =>
    patchWithHeaders<PublicFormAutosaveResponse>(`/api/forms/patient/${token}/autosave`, data),

  submit: (token: string, data: PublicFormSubmitInput) =>
    postWithHeaders<PublicFormSubmitResponse>(`/api/forms/patient/${token}/submit`, data),
};
